import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, LayoutDashboard } from "lucide-react";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Mission", to: "/#mission" },
  { label: "GeneID", to: "/#about-geneid" },
  { label: "Tests", to: "/#tests" },
  { label: "Physician Onboarding", to: "/physician-onboarding" },
];

export function Navbar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const go = (to) => {
    setOpen(false);
    if (to === "/") {
      setOpen(false);
      if (location.pathname !== "/") navigate("/");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 120);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else navigate(to);
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-border" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <button
              key={n.label}
              onClick={() => go(n.to)}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className="text-sm font-medium text-muted-foreground hover:text-navy transition-colors relative after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:w-0 after:bg-patriot hover:after:w-full after:transition-all after:duration-300"
            >
              {n.label}
            </button>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <Button onClick={() => navigate("/dashboard")} data-testid="nav-dashboard-btn" className="rounded-full bg-navy hover:bg-navy-dark text-white gap-2">
              <LayoutDashboard className="h-4 w-4" /> Dashboard
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login-btn" className="text-navy hover:bg-muted hover:text-navy rounded-full">
                Log In
              </Button>
              <Button onClick={() => navigate("/signup")} data-testid="nav-join-btn" className="rounded-full bg-patriot hover:bg-patriot-dark text-white">
                Become a Rep
              </Button>
            </>
          )}
        </div>
        <button className="lg:hidden text-navy" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-border bg-white px-5 py-4 space-y-2" data-testid="nav-mobile-menu">
          {NAV.map((n) => (
            <button key={n.label} onClick={() => go(n.to)} className="block w-full text-left py-2 text-sm font-medium text-muted-foreground hover:text-navy">
              {n.label}
            </button>
          ))}
          <div className="pt-3 flex flex-col gap-2">
            {user ? (
              <Button onClick={() => { setOpen(false); navigate("/dashboard"); }} className="rounded-full bg-navy text-white">Dashboard</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => { setOpen(false); navigate("/login"); }} className="rounded-full border-navy text-navy">Log In</Button>
                <Button onClick={() => { setOpen(false); navigate("/signup"); }} className="rounded-full bg-patriot text-white">Become a Rep</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
